import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../redux/store";
import {
  listenToShortGuestReservations,
} from "../../../../redux/shortstayReservationSlice";
import { listenToMultiShortstays } from "../../../../redux/shortstaysSlice";
import { fetchAllUsers } from "../../../../redux/authSlice";
import { motion } from "framer-motion";
import { Call } from "@mui/icons-material";

const STATUS_LABELS: Record<string, string> = {
  checkingOut: "Checking Out",
  confirmed: "Confirmed Trip",
  upcoming: "Upcoming Trip",
  currentlyHosting: "Hosted",
};

const Review: React.FC = () => {
  const guest = useSelector((state: RootState) => state.auth.user);
  const guestId = guest?.userId;
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const reservations = useSelector(
    (state: RootState) => state.shortstayReservations.guestReservations
  );
  const listings = useSelector((state: RootState) => state.shortstays.listings);
  const allUsers = useSelector((state: RootState) => state.auth.allUsers);

  const [fetchedListings, setFetchedListings] = useState<string[]>([]);

  // --- Fetch guest reservations + users ---
  useEffect(() => {
    if (guestId) {
      dispatch(listenToShortGuestReservations(guestId));
      dispatch(fetchAllUsers());
    }
  }, [dispatch, guestId]);

  // --- Fetch missing listings ---
  useEffect(() => {
    reservations.forEach((r) => {
      if (!fetchedListings.includes(r.listingId)) {
        dispatch(listenToMultiShortstays([r.listingId]));
        setFetchedListings((prev) => [...prev, r.listingId]);
      }
    });
  }, [reservations, fetchedListings, dispatch]);

  const listingsMap = useMemo(() => {
    const map: Record<string, any> = {};
    listings.forEach((l) => (map[l.listingId] = l));
    return map;
  }, [listings]);

  // ✅ Filter only isCheckingOut stays
  const filteredReservations = useMemo(
    () => reservations.filter((r) => r.isCheckingOut && !r.isCancelled),
    [reservations]
  );

  const findHostProfile = (hostId: string) =>
    allUsers.find((u) => u.userId === hostId) || null;

  const handleCallHost = (phoneNumber?: string) => {
    if (phoneNumber) window.open(`tel:${phoneNumber}`);
  };

  return (
    <>
      {filteredReservations.length === 0 ? (
        <p style={styles.noBookings}>No stays ready for review yet.</p>
      ) : (
        filteredReservations.map((res, index) => {
          const listing = listingsMap[res.listingId];
          const hostProfile =
            findHostProfile(res.hostId) ||
            listing?.hostProfile ||
            res.hostProfile ||
            {};
          const hostInitial =
            hostProfile.firstName?.[0]?.toUpperCase() || "H";
          const hostImg = hostProfile.profilePicture;

          return (
            <motion.div
              key={res.id}
              style={styles.card}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              {/* --- Card Header --- */}
              <div style={styles.cardTop}>
                <img
                  src={listing?.photos?.[0] || "/default-stay.jpg"}
                  alt="Listing"
                  style={styles.listingImg}
                />
                <div style={styles.cardText}>
                  <h3 style={styles.title}>
                    {listing?.title || "Unnamed Stay"}
                  </h3>
                  <p style={styles.date}>
                    {new Date(res.checkIn).toLocaleDateString()} →{" "}
                    {new Date(res.checkOut).toLocaleDateString()}
                  </p>
                  <p style={styles.status}>
                    {STATUS_LABELS["checkingOut"]}
                  </p>
                </div>
              </div>

              {/* --- Review Button --- */}
              <button
                onClick={() =>
                  navigate(`/shortrating/${guestId}/${res.listingId}/${res.id}`)
                }
                style={styles.reviewButton}
              >
                Review Your Stay
              </button>

              {/* --- Host Card --- */}
              <div style={styles.hostCard}>
                {hostImg ? (
                  <img
                    src={hostImg}
                    alt="Host"
                    style={styles.hostAvatar}
                    onClick={() => navigate(`/aboutme/${res.hostId}`)}
                  />
                ) : (
                  <div
                    style={styles.hostInitialCircle}
                    onClick={() => navigate(`/aboutme/${res.hostId}`)}
                  >
                    {hostInitial}
                  </div>
                )}

                <div style={{ flex: 1 }}>
                  <p style={styles.hostName}>
                    {hostProfile.firstName || "Host"}{" "}
                    {hostProfile.lastName || ""}
                  </p>
                  <p style={styles.hostLabel}>Your Host</p>
                </div>

                <div style={styles.actions}>
                  {/* Message */}
                  <button
                    style={styles.messageBtn}
                    onClick={() => navigate(`/messages/${res.hostId}`)}
                  >
                    💬 Message
                  </button>

                  {/* Call */}
                  {hostProfile.phoneNumber && (
                    <button
                      style={styles.callBtn}
                      onClick={() => handleCallHost(hostProfile.phoneNumber)}
                    >
                      <Call style={{ fontSize: 20, color: "#bf9b30" }} />
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })
      )}
    </>
  );
};

export default Review;

/* ---------- STYLES ---------- */
const styles: { [key: string]: React.CSSProperties } = {
  noBookings: {
    textAlign: "center",
    color: "#888",
    marginTop: "3rem",
  },
  card: {
    background: "#fff",
    border: "1px solid #eee",
    borderRadius: "18px",
    boxShadow: "0 8px 18px rgba(0,0,0,0.08)",
    padding: "16px",
    marginBottom: "24px",
    transition: "all 0.25s ease",
  },
  cardTop: {
    display: "flex",
    gap: "1rem",
    marginBottom: "0.75rem",
  },
  listingImg: {
    width: 100,
    height: 100,
    borderRadius: "12px",
    objectFit: "cover",
  },
  cardText: { flex: 1 },
  title: { fontSize: "1rem", fontWeight: 600, marginBottom: 6 },
  date: { fontSize: "0.9rem", color: "#555", marginBottom: 4 },
  status: { fontSize: "0.85rem", fontStyle: "italic", color: "#777" },
  reviewButton: {
    background: "linear-gradient(145deg, #fff, #d4af37)",
    color: "#000",
    border: "none",
    borderRadius: 25,
    fontWeight: 600,
    padding: "10px 20px",
    width: "100%",
    cursor: "pointer",
    boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
    transition: "all 0.2s ease",
  },
  hostCard: {
    marginTop: "14px",
    background: "#fafafa",
    borderRadius: "12px",
    padding: "10px 14px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
  },
  hostAvatar: {
    width: 50,
    height: 50,
    borderRadius: "50%",
    objectFit: "cover",
    cursor: "pointer",
    marginRight: 10,
  },
  hostInitialCircle: {
    width: 50,
    height: 50,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #111, #555)",
    color: "#fff",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontWeight: 700,
    fontSize: "16px",
    cursor: "pointer",
    marginRight: 10,
  },
  hostName: { fontSize: "0.95rem", fontWeight: 600, marginBottom: 2 },
  hostLabel: { fontSize: "0.8rem", color: "#777" },
  actions: { display: "flex", alignItems: "center", gap: "8px" },
  messageBtn: {
    background: "#111",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "8px 14px",
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
  },
  callBtn: {
    background: "transparent",
    border: "1px solid #bf9b30",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
  },
};

